import {
  CellContext,
  ColumnDef,
  ColumnDefTemplate,
  ColumnSizingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  Row,
  RowData,
  Table,
  TableOptions,
  TableState,
  useReactTable,
} from '@tanstack/react-table';
import { TEST_IDS } from '@va/constants';
import { useTranslate } from '@va/localization';
import { HorizontalScroll } from '@va/ui/design-system';
import classNames, { ArgumentArray } from 'classnames';
import { CSSProperties, memo, ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import Skeleton from 'react-loading-skeleton';
import { SegmentCellV8, SegmentNameCellV8 } from './cells/SegmentCellV8';
import { useColumnSizingContext } from './column-sizing/ColumnSizingContext';
import { useColumnResizeIndicator } from './column-sizing/useColumnResizeIndicator';
import { ColumnLabelFeature } from './column-visibility/ColumnLabelFeature';
import { useColumnVisibilityContext } from './column-visibility/ColumnVisibilityContext';
import { ColumnResizeHandle } from './ColumnResizeHandle';
import './data-table-v8.scss';
import { PaginationV8 } from './PaginationV8';
import { useClampedPage } from './useClampedPage';

const DEFAULT_COLUMN_MIN_SIZE = 210;

declare module '@tanstack/table-core' {
  interface TableMeta<TData extends RowData> {
    id: string;
    defaultSorting?: TableSorting<keyof TData & string>[];
  }
}

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    cellClassName?: string;
    cellAlignment?: 'left' | 'center' | 'right';
    useDefaultCellStyle?: boolean;
    /** Display label for the column, used in column visibility dropdown to overwrite the default one */
    label?: string;
    /**
     * A custom cell template for a segment cell. If provided, it will be used instead of the `cell` template.
     * - The template will receive the same context as the `cell` template, plus the `segmentData` property, which contains the data of the segment.
     * - The `getValue` function returns the value from the `segmentData` object by the `accessorKey` property of the column.
     * - The `row.original` contains the original data of the row (not the segment data)
     */
    segmentCell?: TData extends { segments: TableSegment<TData>[] }
      ? ColumnDefTemplate<CellContext<TData, TValue> & { segmentData: TData }>
      : never;
  }
}

export type TableSegment<T> = {
  id: string;
  name?: string;
  data: T;
};

export type TableSorting<T extends string = string> = {
  id: T;
  desc: boolean;
};

type DataTableV8Props<T extends { segments?: TableSegment<T>[] }> = {
  id: string;
  isLoading?: boolean;
  data: T[];
  columns: ColumnDef<T, any>[];
  className?: string;
  tableStyle?: CSSProperties;
  rowCount?: number;
  pageCount?: number;
  paginationContainerId?: string;
  disablePagination?: boolean;
  state?: Partial<TableState>;
  enableHorizontalScrolling?: boolean;
  displayTableHeader?: boolean;
  displayBottomPagination?: boolean;
  manualPagination?: TableOptions<T>['manualPagination'];
  manualSorting?: TableOptions<T>['manualSorting'];
  enableMultiRowSelection?: TableOptions<T>['enableMultiRowSelection'];
  initialState?: TableOptions<T>['initialState'];
  onSortingChange?: TableOptions<T>['onSortingChange'];
  onGroupingChange?: TableOptions<T>['onGroupingChange'];
  onPaginationChange?: TableOptions<T>['onPaginationChange'];
  onRowSelectionChange?: TableOptions<T>['onRowSelectionChange'];
  onColumnVisibilityChange?: TableOptions<T>['onColumnVisibilityChange'];
  getRowCanExpand?: TableOptions<T>['getRowCanExpand'];
  getExpandedRowComponent?: (row: Row<T>) => ReactNode;
  onRowClick?: (row: Row<T>) => void;
  noDataMessage?: (() => ReactNode) | ReactNode;
  minWidth?: number;
  getRowId?: (row: T) => string;
  size?: 'small' | 'large';
  stickyHeader?: boolean;
  defaultSorting?: TableSorting[];
};

export const DataTableV8 = <T extends Record<string, any>>({
  id,
  columns,
  data,
  isLoading,
  enableHorizontalScrolling = true,
  enableMultiRowSelection = false,
  displayTableHeader = true,
  manualPagination = true,
  manualSorting = true,
  rowCount,
  className,
  tableStyle,
  pageCount,
  paginationContainerId,
  disablePagination = false,
  state,
  initialState,
  onSortingChange,
  onGroupingChange,
  onPaginationChange,
  onRowSelectionChange,
  onColumnVisibilityChange,
  getRowCanExpand,
  getExpandedRowComponent,
  onRowClick,
  noDataMessage,
  minWidth,
  getRowId = (row) => row.id,
  size = 'large',
  stickyHeader = false,
  displayBottomPagination = false,
  defaultSorting = [],
}: DataTableV8Props<T>) => {
  const {
    columnsRef,
    isProvided: isColumnVisibilityProvided,
    columnVisibility: contextColumnVisibility,
    onColumnVisibilityChange: onContextColumnVisibilityChange,
  } = useColumnVisibilityContext();

  const { isProvided: isColumnSizingProvided, columnSizing, onColumnSizingChange } = useColumnSizingContext();

  const tableRef = useRef<Table<T>>(null as unknown as Table<T>);

  const clampedOnColumnSizingChange = useCallback<OnChangeFn<ColumnSizingState>>(
    (updater) => {
      onColumnSizingChange((prev) => {
        const raw = typeof updater === 'function' ? updater(prev) : updater;
        const clamped: ColumnSizingState = {};
        for (const [colId, size] of Object.entries(raw)) {
          const col = tableRef.current?.getColumn(colId);
          const minSize = col?.columnDef.minSize ?? 0;
          clamped[colId] = Math.max(size, minSize);
        }
        return clamped;
      });
    },
    [onColumnSizingChange],
  );

  const resolvedColumnVisibility =
    state?.columnVisibility ?? (isColumnVisibilityProvided ? contextColumnVisibility : undefined);

  const resolvedState = {
    ...(resolvedColumnVisibility ? { ...state, columnVisibility: resolvedColumnVisibility } : state),
    ...(isColumnSizingProvided ? { columnSizing } : {}),
  };
  const resolvedOnColumnVisibilityChange =
    onColumnVisibilityChange ?? (isColumnVisibilityProvided ? onContextColumnVisibilityChange : undefined);

  const table = useReactTable<T>({
    _features: [ColumnLabelFeature],
    meta: { id, defaultSorting },
    columns: columns,
    data: data,
    getRowCanExpand,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    defaultColumn: {
      enableSorting: false,
      sortDescFirst: false,
      enableGrouping: false,
      enableMultiSort: false,
      enableColumnFilter: false,
      size: DEFAULT_COLUMN_MIN_SIZE,
      minSize: DEFAULT_COLUMN_MIN_SIZE,
      // By default, the max size is Number.MAX_SAFE_INTEGER, which causes a bug with incorrect width in Mozilla Firefox. The value of 10000 is picked so it's not large enough to cause this bug, and the column has enough spare space to display its content.
      maxSize: 10000,
    },
    columnResizeMode: 'onChange',
    enableMultiRowSelection: enableMultiRowSelection,
    manualPagination: manualPagination,
    manualSorting: manualSorting,
    rowCount: rowCount,
    pageCount: pageCount,
    initialState: initialState,
    state: resolvedState,
    onSortingChange: onSortingChange,
    onGroupingChange: onGroupingChange,
    onPaginationChange: onPaginationChange,
    onRowSelectionChange: onRowSelectionChange,
    onColumnVisibilityChange: resolvedOnColumnVisibilityChange,
    onColumnSizingChange: isColumnSizingProvided ? clampedOnColumnSizingChange : undefined,
    getRowId,
  });

  tableRef.current = table;
  columnsRef.current = table.getAllLeafColumns();
  useClampedPage(table);

  return (
    <>
      {paginationContainerId && <PaginationV8 containerId={paginationContainerId} table={table} size={size} />}

      {!paginationContainerId && !disablePagination && <TopPagination table={table} size={size} />}

      {enableHorizontalScrolling && (
        <HorizontalScroll>
          <TableComponent
            table={table}
            displayTableHeader={displayTableHeader}
            isLoading={isLoading}
            className={className}
            noDataMessage={noDataMessage}
            onRowClick={onRowClick}
            style={tableStyle}
            minWidth={minWidth}
            stickyHeader={stickyHeader}
            size={size}
            getExpandedRowComponent={getExpandedRowComponent}
          />
        </HorizontalScroll>
      )}

      {!enableHorizontalScrolling && (
        <TableComponent
          table={table}
          displayTableHeader={displayTableHeader}
          isLoading={isLoading}
          className={className}
          noDataMessage={noDataMessage}
          onRowClick={onRowClick}
          style={tableStyle}
          minWidth={minWidth}
          stickyHeader={stickyHeader}
          size={size}
          getExpandedRowComponent={getExpandedRowComponent}
        />
      )}

      {!disablePagination && <BottomPagination table={table} size={size} forceShow={displayBottomPagination} />}
    </>
  );
};

export const TableComponent = <T extends object>({
  table,
  isLoading,
  displayTableHeader = true,
  className,
  noDataMessage,
  onRowClick,
  style,
  minWidth,
  size = 'large',
  stickyHeader = false,
  onRowMouseEnter,
  onRowMouseLeave,
  getExpandedRowComponent,
}: {
  table: Table<T>;
  isLoading?: boolean;
  displayTableHeader?: boolean;
  className?: string;
  noDataMessage?: (() => ReactNode) | ReactNode;
  onRowClick?: (row: Row<T>) => void;
  style?: CSSProperties;
  minWidth?: number;
  size?: 'small' | 'large';
  stickyHeader?: boolean;
  onRowMouseEnter?: (row: Row<T>) => void;
  onRowMouseLeave?: (row: Row<T>) => void;
  getExpandedRowComponent?: (row: Row<T>) => ReactNode;
}) => {
  const translate = useTranslate();
  const tableId = table.options.meta?.id;
  const {
    isProvided: isColumnSizingEnabled,
    columnSizing,
    onColumnSizingChange,
    storedColumnSizing,
  } = useColumnSizingContext();
  const headerRefs = useRef<Map<string, HTMLTableCellElement>>(new Map());

  const tableElRef = useRef<HTMLTableElement>(null);
  const tableInstanceRef = useRef(table);
  tableInstanceRef.current = table;

  const [needsResnap, setNeedsResnap] = useState(false);

  // Pass 1: on visibility change (and mount), drop state-only sizes so non-stored columns re-flex via 1fr.
  // User-resized columns (those present in storedColumnSizing) are preserved.
  useLayoutEffect(() => {
    if (!isColumnSizingEnabled) return;
    onColumnSizingChange((prev) => {
      const next: ColumnSizingState = {};
      for (const [colId, size] of Object.entries(prev)) {
        if (storedColumnSizing[colId] !== undefined) next[colId] = size;
      }
      return next;
    }, true);
    setNeedsResnap(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.getState().columnVisibility]);

  // Pass 2: after pass 1's re-flex commits, snapshot the fresh rendered widths into state-only sizing
  // so the next drag starts from the actual width (prevents jump on first drag).
  useLayoutEffect(() => {
    if (!needsResnap || !isColumnSizingEnabled) return;

    const visibleCols = tableInstanceRef.current.getVisibleLeafColumns();
    const lastColId = visibleCols[visibleCols.length - 1]?.id;

    const unsized: ColumnSizingState = {};
    for (const [colId, el] of headerRefs.current.entries()) {
      if (colId !== lastColId && storedColumnSizing[colId] === undefined) unsized[colId] = el.offsetWidth;
    }

    onColumnSizingChange((prev) => ({ ...prev, ...unsized }), true);
    setNeedsResnap(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsResnap]);

  const { gradientLineRef, isResizingColumnId } = useColumnResizeIndicator({
    table,
    headerRefs,
    isColumnSizingEnabled,
    tableElRef,
  });

  const resizedColumnsKey = Object.keys(columnSizing).sort().join(',');
  const visibleColumnsKey = table
    .getVisibleLeafColumns()
    .map((c) => c.id)
    .join(',');

  const columnSizeVars = useMemo(() => {
    if (!isColumnSizingEnabled) return {};
    const vars: Record<string, number> = {};
    for (const col of table.getAllLeafColumns()) {
      vars[`--col-${col.id}-size`] = col.getSize();
    }
    return vars;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.getState().columnSizingInfo, table.getState().columnSizing, table.getState().columnVisibility]);

  const gridTemplateColumns = useMemo(() => {
    const visibleColumns = table.getVisibleLeafColumns();
    if (!isColumnSizingEnabled) {
      return visibleColumns
        .map((col) => `minmax(${col.columnDef.minSize ?? DEFAULT_COLUMN_MIN_SIZE}px, 1fr)`)
        .join(' ');
    }

    return visibleColumns
      .map((col, i) => {
        const minSize = col.columnDef.minSize ?? DEFAULT_COLUMN_MIN_SIZE;
        // The last visible column can only have its minWidth configured, to preserve table flexibility
        const isLast = i === visibleColumns.length - 1;
        if (isLast) return `minmax(calc(var(--col-${col.id.replaceAll(' ', '\\ ')}-size) * 1px), 1fr)`;
        return columnSizing[col.id] !== undefined
          ? // Handle empty spaces inside IDs
            `calc(var(--col-${col.id.replaceAll(' ', '\\ ')}-size) * 1px)`
          : `minmax(${minSize}px, 1fr)`;
      })
      .join(' ');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizedColumnsKey, visibleColumnsKey, isColumnSizingEnabled]);

  return (
    <>
      {/* Render all column headers (including hidden) to populate labels via setLabel() */}
      <div className='hidden' aria-hidden='true'>
        {table.getAllLeafColumns().map((col) => {
          if (typeof col.columnDef.header !== 'function') return null;
          try {
            return <span key={col.id}>{col.columnDef.header({ column: col, table } as any)}</span>;
          } catch {
            return null;
          }
        })}
      </div>
      <div className='relative'>
        <table
          ref={tableElRef}
          className={classNames(
            'data-table-v8 grid w-full',
            `size-${size}`,
            { 'overflow-y-auto scrollbar scrollbar-thumb relative': stickyHeader },
            className,
          )}
          style={{ ...columnSizeVars, gridTemplateColumns, minWidth: minWidth, ...(style ?? {}) }}
          data-testid={tableId ? TEST_IDS.helpers.createTableId(tableId) : undefined}
        >
          {displayTableHeader && (
            <thead className='contents'>
              {table.getHeaderGroups().map((headerGroup) => {
                return (
                  <tr className='contents' role='row' key={headerGroup.id}>
                    {headerGroup.headers.map((header, headerIndex) => {
                      const isLeafHeader = !header.isPlaceholder && header.subHeaders.length === 0;
                      const isLastColumn = headerIndex === headerGroup.headers.length - 1;
                      const resizeHandler = isLastColumn
                        ? (e: unknown) => {
                            const width = headerRefs.current.get(header.column.id)?.offsetWidth;
                            if (width) {
                              flushSync(() =>
                                onColumnSizingChange((prev) => ({ ...prev, [header.column.id]: width }), true),
                              );
                            }
                            header.getResizeHandler()(e);
                          }
                        : header.getResizeHandler();
                      return (
                        <th
                          role='rowheader'
                          key={header.id}
                          ref={(el) => {
                            if (!isLeafHeader) return;
                            if (el) headerRefs.current.set(header.column.id, el);
                            else headerRefs.current.delete(header.column.id);
                          }}
                          style={header.colSpan > 1 ? { gridColumn: `span ${header.colSpan}` } : undefined}
                          className={classNames('relative group/header', { 'sticky top-0': stickyHeader })}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                          {isColumnSizingEnabled && isLeafHeader && (
                            <ColumnResizeHandle
                              isResizing={header.column.getIsResizing()}
                              resizeHandler={resizeHandler}
                              canResize={header.column.getCanResize()}
                            />
                          )}
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
          )}
          <tbody className='contents'>
            <MemoizedTableBodyRows
              table={table}
              isLoading={isLoading}
              onRowClick={onRowClick}
              onRowMouseEnter={onRowMouseEnter}
              onRowMouseLeave={onRowMouseLeave}
              getExpandedRowComponent={getExpandedRowComponent}
            />
          </tbody>
        </table>
        {isColumnSizingEnabled && !!isResizingColumnId && (
          <div
            ref={gradientLineRef}
            className='absolute top-0 h-full w-1.5px pointer-events-none z-10 bg-[linear-gradient(to_bottom,rgba(150,150,150,0.35),transparent)]'
          />
        )}
      </div>
      {!isLoading && table.getRowCount() === 0 && (
        <div
          className={`flex items-center justify-center text-mine-shaft font-medium text-sm text-center min-h-87.5 min-w-120px`}
        >
          {typeof noDataMessage === 'function' ? noDataMessage() : (noDataMessage ?? translate('table.noData'))}
        </div>
      )}
    </>
  );
};

type TableBodyRowsProps<T extends { segments?: TableSegment<T>[] }> = {
  table: Table<T>;
  isLoading?: boolean;
  onRowClick?: (row: Row<T>) => void;
  onRowMouseEnter?: (row: Row<T>) => void;
  onRowMouseLeave?: (row: Row<T>) => void;
  getExpandedRowComponent?: (row: Row<T>) => ReactNode;
};

const TableBodyRows = <T extends { segments?: TableSegment<T>[] }>({
  table,
  isLoading,
  onRowClick,
  onRowMouseEnter,
  onRowMouseLeave,
  getExpandedRowComponent,
}: TableBodyRowsProps<T>) => (
  <>
    {isLoading &&
      Array.from(Array(table.getState().pagination.pageSize).keys()).map((_, i) => (
        <tr className='contents group' role='row' key={`tr-skeleton-${i}`}>
          <td style={{ padding: 0, gridColumn: `span ${table.getVisibleLeafColumns().length}` }}>
            <Skeleton className='h-16.75 rounded-12' />
          </td>
        </tr>
      ))}
    {!isLoading &&
      table.getRowCount() > 0 &&
      table
        .getRowModel()
        .rows.map((row) => (
          <TableRow
            key={row.id}
            row={row}
            onClick={onRowClick}
            onMouseEnter={onRowMouseEnter}
            onMouseLeave={onRowMouseLeave}
            getExpandedRowComponent={getExpandedRowComponent}
          />
        ))}
  </>
);

const MemoizedTableBodyRows = memo(TableBodyRows, (prev, next) => {
  // Outside of resize, behave like a bare component — always re-render.
  if (!next.table.getState().columnSizingInfo.isResizingColumn) return false;
  // During resize, suppress re-renders so cells (and their icons) stay mounted (to avoid flickering).
  return prev.table.options.data === next.table.options.data && prev.isLoading === next.isLoading;
}) as typeof TableBodyRows;

const TableRow = <T extends { segments?: TableSegment<T>[] }>({
  row,
  onClick,
  onMouseEnter,
  onMouseLeave,
  getExpandedRowComponent,
}: {
  row: Row<T>;
  onClick?: (row: Row<T>) => void;
  onMouseEnter?: (row: Row<T>) => void;
  onMouseLeave?: (row: Row<T>) => void;
  getExpandedRowComponent?: (row: Row<T>) => ReactNode;
}) => {
  return (
    <>
      <tr
        className={classNames('contents group', {
          'cursor-pointer': !!onClick,
          selected: row.getIsSelected(),
        })}
        role='row'
        key={row.id}
        onClick={() => {
          row.toggleSelected();
          row.toggleExpanded();
          onClick?.(row);
        }}
        onMouseEnter={() => {
          onMouseEnter?.(row);
        }}
        onMouseLeave={() => {
          onMouseLeave?.(row);
        }}
      >
        {row.getVisibleCells().map((cell) => {
          const tdClassname: ArgumentArray = [cell.column.columnDef.meta?.cellClassName];
          const alignment = cell.column.columnDef.meta?.cellAlignment ?? 'center';

          if (cell.column.columnDef.meta?.useDefaultCellStyle !== false) {
            tdClassname.push('w-full flex items-center');
            tdClassname.push({
              'justify-left text-left': alignment === 'left',
              'justify-center text-center': alignment === 'center',
              'justify-right text-right': alignment === 'right',
            });
          }

          return (
            <td
              className={classNames(tdClassname)}
              data-testid={TEST_IDS.helpers.createTableCellId(cell.column.id)}
              key={cell.id}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          );
        })}
      </tr>
      {row.getIsExpanded() && getExpandedRowComponent && (
        <tr className='contents group'>
          <td className='w-full' style={{ gridColumn: `span ${row.getVisibleCells().length}` }}>
            {getExpandedRowComponent?.(row)}
          </td>
        </tr>
      )}
      {row.original.segments?.map((segment) => {
        return (
          <SegmentRow
            segment={segment}
            key={`segment-${segment.id}-${row.id}`}
            name={segment.name ?? segment.id}
            row={row}
          />
        );
      })}
    </>
  );
};

export const TopPagination = ({ table, size = 'large' }: { table: Table<any>; size?: 'small' | 'large' }) => {
  return (
    <div className='flex max-h-12 gap-18px items-center'>
      <PaginationV8
        table={table}
        showCountInfo={true}
        showPageSizeSelector={true}
        showPaginationBtns={true}
        size={size}
      />
    </div>
  );
};

const SegmentRow = <T,>({
  name,
  row,
  segment,
  onClick,
}: {
  name: string;
  row: Row<T>;
  segment: TableSegment<T>;
  onClick?: (row: Row<T>) => void;
}) => {
  const { getVisibleCells } = row;
  const cells = getVisibleCells();
  return (
    <tr
      className={classNames('contents group', {
        'cursor-pointer': !!onClick,
        selected: row.getIsSelected(),
      })}
      role='row'
      onClick={() => {
        row.toggleSelected();
        onClick?.(row);
      }}
    >
      {cells.map((cell, index) => {
        if (index === 0) return <SegmentNameCellV8 name={name} key={`cell-${index}`} />;
        return <SegmentCellV8 key={`cell-${index}`} cell={cell} segmentData={segment.data} />;
      })}
    </tr>
  );
};

export const BottomPagination = ({
  table,
  size = 'large',
  forceShow = false,
}: {
  table: Table<any>;
  size?: 'small' | 'large';
  // Bottom pagination is by default hidden on large screens
  forceShow: boolean;
}) => {
  return (
    <div className={classNames({ 'md:hidden': !forceShow }, 'flex justify-end mt-3')}>
      <PaginationV8
        table={table}
        showCountInfo={forceShow}
        showPageSizeSelector={forceShow}
        showPaginationBtns={true}
        size={size}
      />
    </div>
  );
};
